/**
 * Hook for persisting and loading wizard state to/from Supabase
 */

import { useCallback, useEffect, useState } from 'react';
import { supabase as supabaseClient } from '@/integrations/supabase/client';
import { wizardStore, type WizardState } from '@/stores/wizardStore';
import { toast } from './use-toast';

const supabase = supabaseClient as any;

export function useWizardPersistence() {
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [savedScenarios, setSavedScenarios] = useState<Array<{
    id: string;
    created_at: string;
    updated_at: string;
    meta: any;
  }>>([]);

  // Load saved scenarios list
  const loadScenariosList = useCallback(async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('wizard_scenarios')
        .select('id, created_at, updated_at, meta')
        .order('updated_at', { ascending: false })
        .limit(10);

      if (error) throw error;
      setSavedScenarios(data || []);
    } catch (err) {
      console.error('Failed to load scenarios:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Save current wizard state
  const saveScenario = useCallback(async (name?: string) => {
    try {
      setSaving(true);
      const state = wizardStore.getState();

      const scenarioData = {
        environment: state.environment,
        personas: state.personas,
        model_selections: state.modelSelections,
        progress: state.progress,
        meta: {
          name: name || `Configuration ${new Date().toLocaleDateString()}`,
          savedAt: new Date().toISOString(),
        },
      };

      let result;
      if (state.id) {
        // Update existing scenario
        const { data, error } = await supabase
          .from('wizard_scenarios')
          .update(scenarioData)
          .eq('id', state.id)
          .select()
          .single();

        if (error) throw error;
        result = data;
      } else {
        // Create new scenario
        const { data, error } = await supabase
          .from('wizard_scenarios')
          .insert([scenarioData])
          .select()
          .single();

        if (error) throw error;
        result = data;
        
        // Update store with new ID
        wizardStore.setState({ id: result.id });
      }

      toast({
        title: 'Configuration Saved',
        description: 'Your wizard configuration has been saved successfully.',
      });

      await loadScenariosList();
      return result;
    } catch (err) {
      console.error('Failed to save scenario:', err);
      toast({
        title: 'Save Failed',
        description: 'Could not save your configuration. Please try again.',
        variant: 'destructive',
      });
      throw err;
    } finally {
      setSaving(false);
    }
  }, [loadScenariosList]);

  // Load a specific scenario
  const loadScenario = useCallback(async (scenarioId: string) => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('wizard_scenarios')
        .select('*')
        .eq('id', scenarioId)
        .single();

      if (error) throw error;

      if (data) {
        const loadedState: WizardState = {
          id: data.id,
          environment: data.environment as any,
          personas: data.personas as any,
          modelSelections: data.model_selections as any,
          progress: data.progress as any,
        };

        wizardStore.loadState(loadedState);

        const scenarioName = (data.meta as any)?.name || 'Saved configuration';
        toast({
          title: 'Configuration Loaded',
          description: `Loaded: ${scenarioName}`,
        });

        return loadedState;
      }
    } catch (err) {
      console.error('Failed to load scenario:', err);
      toast({
        title: 'Load Failed',
        description: 'Could not load the configuration. Please try again.',
        variant: 'destructive',
      });
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Delete a scenario
  const deleteScenario = useCallback(async (scenarioId: string) => {
    try {
      const { error } = await supabase
        .from('wizard_scenarios')
        .delete()
        .eq('id', scenarioId);

      if (error) throw error;

      toast({
        title: 'Configuration Deleted',
        description: 'The saved configuration has been removed.',
      });

      await loadScenariosList();
    } catch (err) {
      console.error('Failed to delete scenario:', err);
      toast({
        title: 'Delete Failed',
        description: 'Could not delete the configuration. Please try again.',
        variant: 'destructive',
      });
      throw err;
    }
  }, [loadScenariosList]);

  // Auto-save effect (debounced)
  useEffect(() => {
    const state = wizardStore.getState();
    if (!state.id) return; // Only auto-save if already saved once

    const timeoutId = setTimeout(() => {
      // Auto-save silently in background
      saveScenario().catch(() => {
        // Silently fail auto-save
      });
    }, 30000); // Auto-save after 30 seconds of inactivity

    return () => clearTimeout(timeoutId);
  }, [saveScenario]);

  // Load scenarios list on mount
  useEffect(() => {
    loadScenariosList();
  }, [loadScenariosList]);

  return {
    saving,
    loading,
    savedScenarios,
    saveScenario,
    loadScenario,
    deleteScenario,
    loadScenariosList,
  };
}
